import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Zap, Plus, X, Check, Loader2, Info } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

export function RewardRulesPage() {
  const { worldId } = useParams<{ worldId: string }>();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'ADMIN_READONLY';
  const [showForm, setShowForm] = useState(false);
  const [eventType, setEventType] = useState('');
  const [name, setName] = useState('');
  const [baseAmount, setBaseAmount] = useState('');
  const [cooldown, setCooldown] = useState('3600');
  const [dailyCap, setDailyCap] = useState('1000');
  const [direction, setDirection] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [gameReward, setGameReward] = useState('');

  const { data: world } = useQuery({
    queryKey: ['world', worldId],
    queryFn: () => api.get(`/worlds/${worldId}`).then((r) => r.data),
  });

  const { data: cbConfig } = useQuery<{ tipRevSharePct: string }>({
    queryKey: ['cb-config'],
    queryFn: () => api.get('/central-bank/config').then((r) => r.data),
  });

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['rules', worldId],
    queryFn: () => api.get(`/worlds/${worldId}/rules`).then((r) => r.data),
  });

  const RULES_PER_PAGE = 25;
  const [rulePage, setRulePage] = useState(1);
  const totalRulePages = Math.max(1, Math.ceil(rules.length / RULES_PER_PAGE));
  const paginatedRules = useMemo(
    () => rules.slice((rulePage - 1) * RULES_PER_PAGE, rulePage * RULES_PER_PAGE),
    [rules, rulePage],
  );

  const symbol = world?.currencySymbol ?? 'UKC';

  const fees = useMemo(() => {
    if (!baseAmount || !world || direction !== 'CREDIT') return null;
    const amt = parseFloat(baseAmount);
    if (!amt || amt <= 0) return null;
    const revSharePct = parseFloat(String(world.revenueShare ?? '0')) / 100;
    const cbCutPct = parseFloat(cbConfig?.tipRevSharePct ?? '30') / 100;
    const taxPct = parseFloat(String(world.taxShare ?? '0')) / 100;
    const worldRevChunk = amt * revSharePct;
    const cbShare = worldRevChunk * cbCutPct;
    const partnerShare = worldRevChunk - cbShare;
    const afterRevShare = amt - worldRevChunk;
    const taxAmount = taxPct > 0 ? afterRevShare * taxPct : 0;
    const playerReceives = afterRevShare - taxAmount;
    return { worldRevChunk, cbShare, partnerShare, taxAmount, playerReceives, revSharePct, cbCutPct, taxPct };
  }, [baseAmount, world, cbConfig, direction]);

  const createMutation = useMutation({
    mutationFn: (data: object) => api.post(`/worlds/${worldId}/rules`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules', worldId] });
      setShowForm(false);
      setEventType(''); setName(''); setBaseAmount(''); setDirection('CREDIT'); setGameReward('');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (ruleId: string) => api.patch(`/worlds/${worldId}/rules/${ruleId}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules', worldId] }),
  });

  const activateMutation = useMutation({
    mutationFn: (ruleId: string) => api.patch(`/worlds/${worldId}/rules/${ruleId}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules', worldId] }),
  });

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/worlds/${worldId}`} className="text-slate-600 hover:text-slate-300 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/15 to-orange-500/15 border border-amber-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Reward Rules</h2>
              <p className="text-xs text-slate-600">{world?.name} &mdash; rewards paid in <span className="text-amber-400 font-semibold">{symbol}</span></p>
            </div>
          </div>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl btn-primary"
          >
            <Plus className="w-3.5 h-3.5" /> New Rule
          </button>
        )}
      </div>

      {/* Immutability notice */}
      <div className="flex items-start gap-3 glass-card p-4" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
        <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          Rules are <span className="text-slate-400 font-medium">permanent</span> — they cannot be edited after creation.
          Use <span className="text-emerald-400 font-medium">Activate</span> &#47; <span className="text-red-400 font-medium">Deactivate</span> to control when a rule is applied.
        </p>
      </div>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 space-y-4 overflow-hidden"
            style={{ borderColor: 'rgba(0,212,255,0.2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate({
                eventType,
                name: name || eventType,
                direction,
                baseAmount: parseFloat(baseAmount),
                cooldownSeconds: parseInt(cooldown),
                dailyCap: parseFloat(dailyCap),
                gameReward: gameReward || undefined,
              });
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">New Reward Rule</p>

            {/* Direction toggle */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Direction</label>
              <div className="inline-flex rounded-xl overflow-hidden border border-white/10">
                <button
                  type="button"
                  onClick={() => setDirection('CREDIT')}
                  className={`px-4 py-2 text-xs font-semibold transition-all ${
                    direction === 'CREDIT'
                      ? 'bg-emerald-500/20 text-emerald-300 border-r border-white/10'
                      : 'text-slate-500 hover:text-slate-300 border-r border-white/10'
                  }`}
                >
                  &#x2191; CREDIT
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('DEBIT')}
                  className={`px-4 py-2 text-xs font-semibold transition-all ${
                    direction === 'DEBIT'
                      ? 'bg-red-500/20 text-red-300'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  &#x2193; DEBIT
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-slate-600">
                {direction === 'CREDIT' ? 'Adds currency to the player (reward, earning)' : 'Subtracts currency from the player (purchase, fee, penalty)'}
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Event Type</label>
                <input value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="QUEST_COMPLETE" required className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Display Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Quest Complete" className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Base Amount ({symbol})</label>
                <input type="number" min="0.000001" step="any" value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} placeholder="100" required className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Cooldown (seconds)</label>
                <input type="number" min="0" value={cooldown} onChange={(e) => setCooldown(e.target.value)} placeholder="3600" required className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Daily Max Per Player ({symbol})</label>
                <input type="number" min="0" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} placeholder="1000" required className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">In-Game Reward Label <span className="text-slate-700">(optional)</span></label>
                <input value={gameReward} onChange={(e) => setGameReward(e.target.value)} placeholder="e.g. Rare Sword, 500 Gold" className={inputCls} />
              </div>
            </div>

            {/* Fee breakdown */}
            {fees && (
              <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 px-4 py-3 space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-400/70 mb-2">Revenue breakdown per reward</p>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Base reward</span>
                  <span className="text-slate-300 font-mono">{parseFloat(baseAmount).toLocaleString(undefined, { maximumFractionDigits: 4 })} {symbol}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Rev share to world ({(fees.revSharePct * 100).toFixed(1)}%)</span>
                  <span className="text-slate-400 font-mono">− {fees.worldRevChunk.toFixed(4)} {symbol}</span>
                </div>
                <div className="flex justify-between text-xs pl-4">
                  <span className="text-slate-600">↳ CB cut ({(fees.cbCutPct * 100).toFixed(0)}%)</span>
                  <span className="text-slate-600 font-mono">{fees.cbShare.toFixed(4)} {symbol}</span>
                </div>
                <div className="flex justify-between text-xs pl-4">
                  <span className="text-slate-600">↳ Partner share</span>
                  <span className="text-slate-600 font-mono">{fees.partnerShare.toFixed(4)} {symbol}</span>
                </div>
                {fees.taxPct > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Tax ({(fees.taxPct * 100).toFixed(1)}%)</span>
                    <span className="text-slate-400 font-mono">− {fees.taxAmount.toFixed(4)} {symbol}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs border-t border-white/8 pt-1.5 mt-1">
                  <span className="text-emerald-400 font-semibold">Player receives</span>
                  <span className="text-emerald-300 font-mono font-bold">{fees.playerReceives.toFixed(4)} {symbol}</span>
                </div>
              </div>
            )}
            {createMutation.isError && (
              <p className="text-red-400 text-xs">{(createMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Create failed'}</p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowForm(false); setDirection('CREDIT'); setGameReward(''); }} className="text-xs text-slate-400 px-4 py-2 rounded-xl border border-white/8 hover:border-white/15 transition-all">Cancel</button>
              <button type="submit" disabled={createMutation.isPending} className="btn-primary text-xs font-semibold px-5 py-2 rounded-xl flex items-center gap-2">
                {createMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</> : <><Check className="w-3.5 h-3.5" /> Create Rule</>}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Rules table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Event Type', 'Name', 'Direction', `Base (${symbol})`, 'Cooldown', `Daily Max (${symbol})`, 'Game Reward', 'Status', ''].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-600"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></td></tr>
              ) : rules.length === 0 ? (
                <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-600">No reward rules yet &mdash; click New Rule to create one</td></tr>
              ) : (paginatedRules as Record<string, string | boolean>[]).map((r) => (
                <tr key={r['id'] as string} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }} className="table-row-hover">
                  <td className="px-5 py-3 text-amber-300 font-mono font-semibold">{r['eventType'] as string}</td>
                  <td className="px-5 py-3 text-slate-300">{r['name'] as string}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-semibold ${
                      r['direction'] === 'DEBIT'
                        ? 'bg-red-500/15 border border-red-500/25 text-red-400'
                        : 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'
                    }`}>
                      {r['direction'] === 'DEBIT' ? '\u2193' : '\u2191'} {(r['direction'] as string) ?? 'CREDIT'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-300 font-mono">{r['baseAmount'] as string}</td>
                  <td className="px-5 py-3 text-slate-500">{r['cooldownSeconds'] as string}s</td>
                  <td className="px-5 py-3 text-slate-300 font-mono">{r['dailyMaxPerPlayer'] as string}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs">{(r['gameReward'] as string) || <span className="text-slate-700">—</span>}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full font-medium ${
                      r['isActive'] ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400' : 'bg-slate-500/15 border border-slate-500/20 text-slate-500'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${r['isActive'] ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      {r['isActive'] ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {!isReadOnly && (r['isActive'] ? (
                      <button
                        onClick={() => deactivateMutation.mutate(r['id'] as string)}
                        className="text-[10px] font-medium text-red-400 hover:text-red-300 px-2.5 py-1 rounded-lg border border-red-500/20 hover:bg-red-500/10 transition-all"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => activateMutation.mutate(r['id'] as string)}
                        className="text-[10px] font-medium text-emerald-400 hover:text-emerald-300 px-2.5 py-1 rounded-lg border border-emerald-500/20 hover:bg-emerald-500/10 transition-all"
                      >
                        Activate
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalRulePages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {rulePage} of {totalRulePages} &bull; {rules.length} rules
            </span>
            <div className="flex gap-2">
              <button
                disabled={rulePage <= 1}
                onClick={() => setRulePage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={rulePage >= totalRulePages}
                onClick={() => setRulePage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Rule Simulator ── */}
      <RuleSimulator rules={rules} world={world} cbConfig={cbConfig} />
    </div>
  );
}

function RuleSimulator({
  rules,
  world,
  cbConfig,
}: {
  rules: Record<string, unknown>[];
  world: Record<string, unknown> | undefined;
  cbConfig: { tipRevSharePct: string } | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [simAmount, setSimAmount] = useState('');

  const selectedRule = rules.find((r) => r['id'] === selectedRuleId);

  const simFees = useMemo(() => {
    if (!simAmount || !world || !selectedRule) return null;
    const amt = parseFloat(simAmount);
    if (!amt || amt <= 0) return null;
    const wsf = parseFloat(String(world['currentWsf'] ?? '1'));
    const scaledAmt = amt * wsf;
    const revShareOverride = selectedRule['revShareOverride'];
    const taxShareOverride  = selectedRule['taxShareOverride'];
    const revSharePct = parseFloat(String(revShareOverride ?? world['revenueShare'] ?? '0')) / 100;
    const cbCutPct    = parseFloat(cbConfig?.tipRevSharePct ?? '30') / 100;
    const taxPct      = parseFloat(String(taxShareOverride  ?? world['taxShare']    ?? '0')) / 100;
    const worldRevChunk  = scaledAmt * revSharePct;
    const cbShare        = worldRevChunk * cbCutPct;
    const partnerShare   = worldRevChunk - cbShare;
    const afterRevShare  = scaledAmt - worldRevChunk;
    const taxAmount      = taxPct > 0 ? afterRevShare * taxPct : 0;
    const playerReceives = afterRevShare - taxAmount;
    return { scaledAmt, worldRevChunk, cbShare, partnerShare, taxAmount, playerReceives, wsf, revSharePct, cbCutPct, taxPct };
  }, [simAmount, selectedRule, world, cbConfig]);

  const symbol = world?.['currencySymbol'] as string ?? 'UKC';

  return (
    <motion.div
      className="card mt-6 overflow-hidden"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.1 }}
    >
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-white">Rule Simulator</span>
          <span className="text-[10px] text-slate-500 ml-1">— preview reward payouts for any rule</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="sim-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-white/[0.04]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">Select Rule</label>
                  <select
                    className={inputCls}
                    value={selectedRuleId}
                    onChange={(e) => setSelectedRuleId(e.target.value)}
                  >
                    <option value="">— choose a rule —</option>
                    {rules.filter((r) => r['isActive']).map((r) => (
                      <option key={r['id'] as string} value={r['id'] as string}>
                        {r['name'] as string} ({r['eventType'] as string})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1.5">
                    Base Reward Amount ({symbol})
                  </label>
                  <input
                    type="number"
                    className={inputCls}
                    placeholder="e.g. 100"
                    value={simAmount}
                    onChange={(e) => setSimAmount(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              {selectedRule && (
                <div className="text-[11px] text-slate-500 mb-4 flex flex-wrap gap-x-4 gap-y-1">
                  {selectedRule['revShareOverride'] != null && (
                    <span>Rev share override: <span className="text-amber-400">{selectedRule['revShareOverride']}%</span></span>
                  )}
                  {selectedRule['taxShareOverride'] != null && (
                    <span>Tax override: <span className="text-amber-400">{selectedRule['taxShareOverride']}%</span></span>
                  )}
                  <span>Cooldown: {selectedRule['cooldownSeconds']}s</span>
                  <span>Daily cap: {selectedRule['dailyCapAmount']}</span>
                </div>
              )}

              {simFees ? (
                <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 space-y-2 text-xs">
                  {simFees.wsf !== 1 && (
                    <div className="flex justify-between pb-2 mb-1 border-b border-white/[0.04]">
                      <span className="text-slate-500">WSF scaled amount (×{simFees.wsf})</span>
                      <span className="text-white font-mono">{simFees.scaledAmt.toFixed(4)} {symbol}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">World rev share ({(simFees.revSharePct * 100).toFixed(1)}%)</span>
                    <span className="text-amber-400 font-mono">−{simFees.worldRevChunk.toFixed(4)} {symbol}</span>
                  </div>
                  <div className="flex justify-between pl-4">
                    <span className="text-slate-600">→ CB cut ({(simFees.cbCutPct * 100).toFixed(1)}%)</span>
                    <span className="text-rose-400 font-mono">{simFees.cbShare.toFixed(4)} {symbol}</span>
                  </div>
                  <div className="flex justify-between pl-4">
                    <span className="text-slate-600">→ Partner share</span>
                    <span className="text-purple-400 font-mono">{simFees.partnerShare.toFixed(4)} {symbol}</span>
                  </div>
                  {simFees.taxAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tax ({(simFees.taxPct * 100).toFixed(1)}%)</span>
                      <span className="text-orange-400 font-mono">−{simFees.taxAmount.toFixed(4)} {symbol}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-white/[0.06]">
                    <span className="text-white font-semibold">Player receives</span>
                    <span className="text-emerald-300 font-mono font-bold text-sm">{simFees.playerReceives.toFixed(4)} {symbol}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-600 text-center py-4">
                  Select a rule and enter an amount to preview payout breakdown.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
